import React, { Fragment, useEffect, useState } from "react";
import { FormControl, InputGroup, Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import util from "../../utils/miniUtils";
import { ADD_USER, EDIT_USER } from "../../utils/constants";

const validationSchema = Yup.object().shape( {
    firstName: Yup.string( )
        .required( "El nombre es requerido" )
        .min( 2, "Mínimo 2 caracteres" )
        .max( 50, "Máximo 50 caracteres" ),
    lastName: Yup.string( )
        .required( "El apellido es requerido" )
        .min( 2, "Mínimo 2 caracteres" )
        .max( 50, "Máximo 50 caracteres" ),
    email: Yup.string( )
        .required( "El email es requerido" )
        .email( "Email inválido" ),
    phone: Yup.string( )
        .required( "El teléfono es requerido" )
        .matches( /^(\d{4}-\d{2}-\d{2}-\d{2}|\d{10})$/, "El teléfono debe tener formato 6865-26-99-12 o 10 dígitos" ),
    role: Yup.string( )
        .required( "El rol es requerido" )
        .oneOf( [ "1", "2", "3", "4" ], "Rol inválido" )
} );

const passwordValidationSchema = Yup.object().shape( {
    password: Yup.string( )
        .required( "La contraseña es requerida" )
        .min( 8, "Mínimo 8 caracteres" )
        .max( 20, "Máximo 20 caracteres" ),
    passwordConfirmation: Yup.string( )
        .required( "Confirmar contraseña es requerido" )
        .oneOf( [ Yup.ref( "password" ), null ], "Las contraseñas no son iguales" )
} );

export default function UserProfile( { user_id, show, handleClose, reloadTable, action = ADD_USER } ) {

    const [changePassword, setChangePassword] = useState( {
        password: "",
        passwordConfirmation: "",
        show: false,
    } );
    const [credentialsModal, setCredentialsModal] = useState( {
        show: false,
        email: "",
        password: ""
    } );
    const [userStats, setUserStats] = useState( {
        totalIngresados: 0,
        totalRetirados: 0,
        ultimoIngresoFecha: null,
        ultimoRetiroFecha: null
    } );
    const [loading, setLoading] = useState( false );
    const startLoading = () => setLoading( true );
    const stopLoading = () => setLoading( false );

    const formik = useFormik( {
        initialValues: {
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            role: "2"
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: saveUserData
    } );

    useEffect( () => {
        if ( action === EDIT_USER ) {
            getUsersDetail();
            getUserStats();
        }
    }, [ action, user_id ] );

    async function getUsersDetail() {
        try {
            startLoading();
            const request = await util.axios.get( `users/detail/${user_id}` );
            let { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();
            formik.setValues( {
                firstName: data.firstName || "",
                lastName: data.lastName || "",
                phone: data.phone || "",
                email: data.email || "",
                role: String( data.role ) || "2"
            } );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function getUserStats() {
        try {
            const request = await util.axios.get( `users/${user_id}/stats` );
            let { error, data } = request.data;
            if ( error ) throw error;
            setUserStats( data );
        } catch ( e ) {
            util.handleError( e );
        }
    }

    async function saveUserData( values ) {
        try {
            startLoading();
            const userData = {
                ...values,
                role: String( values.role )
            };
            let request;
            if ( action === ADD_USER ) {
                request = await util.axios.post( `users/`, userData );
            } else {
                request = await util.axios.put( `users/${user_id}`, userData );
            }

            let { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();
            
            if ( action === ADD_USER ) {
                // Mostrar modal con credenciales
                setCredentialsModal( {
                    show: true,
                    email: data.email,
                    password: data.password
                } );
            } else {
                util.toast().success( msg );
                reloadTable();
                handleClose();
            }
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function saveNewPassword() {
        try {
            startLoading();
            await passwordValidationSchema.validate( {
                password: changePassword.password,
                passwordConfirmation: changePassword.passwordConfirmation
            } );
            let request = await util.axios.put( `users/change_password/${user_id}`, changePassword );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
            reloadTable();
            handleClose();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    const handleChangePassword = ( e ) => {
        const { name, value } = e.target;
        setChangePassword( { ...changePassword, [name]: value } );
    };

    const getFieldError = ( fieldName ) => {
        return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : null;
    };

    return (
        <Fragment>
            <Modal show={show} onHide={handleClose} size={action === EDIT_USER ? "lg" : "md"}>
                <Modal.Header closeButton>
                    <Modal.Title>Perfil de Usuario</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            {/* Columna Izquierda: Formulario */}
                            <div className={action === EDIT_USER ? "col-6" : "col-12"}>
                                <InputCX
                                    title="Nombre"
                                    name="firstName"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "firstName" )}
                                />
                                <InputCX
                                    title="Apellido"
                                    name="lastName"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "lastName" )}
                                />
                                <InputCX
                                    title="Correo"
                                    name="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "email" )}
                                />
                                <InputCX
                                    title="Teléfono"
                                    name="phone"
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "phone" )}
                                    mask="phone"
                                />
                                <label htmlFor="rol">Rol</label>
                                <InputGroup className="mb-3">
                                    <select
                                        value={formik.values.role}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`form-control ${getFieldError( "role" ) ? "is-invalid" : ""}`}
                                        name="role"
                                        id="rol"
                                    >
                                        <option value="4">Proveedor</option>
                                        <option value="3">Mecánico</option>
                                        <option value="2">Empleado</option>
                                        <option value="1">Admin</option>
                                    </select>
                                </InputGroup>
                                {getFieldError( "role" ) && <span className="text-danger">{getFieldError( "role" )}</span>}

                                <hr />
                                <b className="likeLink" onClick={() => setChangePassword( { ...changePassword, show: !changePassword.show, password: "", passwordConfirmation: "" } )}>
                                    {changePassword.show ? "Cancelar cambio de contraseña" : "Cambiar Contraseña"}
                                </b>
                                {
                                    changePassword.show ?
                                        <Fragment>
                                            <div className="mt-3">
                                                <InputCX title="Contraseña" type="password" name="password" value={changePassword.password} onChange={handleChangePassword} />
                                            </div>
                                            <div>
                                                <InputCX title="Confirmar Contraseña" type="password" name="passwordConfirmation" value={changePassword.passwordConfirmation} onChange={handleChangePassword} />
                                            </div>
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    onClick={saveNewPassword}
                                                    className="btn btn-sm btn-success">
                                                    Cambiar Contraseña
                                                </button>
                                            </div>
                                        </Fragment>
                                        :
                                        null
                                }
                            </div>

                            {/* Columna Derecha: Estadísticas */}
                            {action === EDIT_USER && (
                                <div className="col-6">
                                    <div className="card">
                                        <div className="card-header">
                                            <h6 className="mb-0">📊 Estadísticas</h6>
                                        </div>
                                        <div className="card-body p-3">
                                            <table className="table table-sm table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <td className="fw-bold">Productos Ingresados:</td>
                                                        <td className="text-end"><span className="badge bg-success">{userStats.totalIngresados}</span></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="fw-bold">Productos Retirados:</td>
                                                        <td className="text-end"><span className="badge bg-danger">{userStats.totalRetirados}</span></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="fw-bold">Último Ingreso:</td>
                                                        <td className="text-end text-muted small">
                                                            {userStats.ultimoIngresoFecha 
                                                                ? new Date( userStats.ultimoIngresoFecha ).toLocaleDateString( 'es-MX' )
                                                                : "N/A"
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="fw-bold">Último Retiro:</td>
                                                        <td className="text-end text-muted small">
                                                            {userStats.ultimoRetiroFecha 
                                                                ? new Date( userStats.ultimoRetiroFecha ).toLocaleDateString( 'es-MX' )
                                                                : "N/A"
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-sm" onClick={handleClose}>
                        Cancelar
                    </button>
                    <button type="submit" className="btn btn-sm btn-primary" onClick={formik.handleSubmit}>
                        Guardar Cambios
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Credenciales */}
            <Modal show={credentialsModal.show} onHide={() => {
                setCredentialsModal( { show: false, email: "", password: "" } );
                reloadTable();
                handleClose();
            }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Usuario Creado Exitosamente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-info">
                        <strong>⚠️ Importante:</strong> Esta contraseña será visible solo en esta ocasión. Cópiala y envíala al usuario por el método que prefieras (WhatsApp, correo, etc).
                    </div>
                    
                    <div className="mb-3">
                        <label className="fw-bold">Correo:</label>
                        <div className="input-group">
                            <input 
                                type="text" 
                                className="form-control" 
                                value={credentialsModal.email} 
                                readOnly 
                            />
                            <button 
                                className="btn btn-outline-secondary" 
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText( credentialsModal.email );
                                    util.toast().success( 'Correo copiado al portapapeles' );
                                }}
                            >
                                📋 Copiar
                            </button>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="fw-bold">Contraseña:</label>
                        <div className="input-group">
                            <input 
                                type="text" 
                                className="form-control" 
                                value={credentialsModal.password} 
                                readOnly 
                            />
                            <button 
                                className="btn btn-outline-secondary" 
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText( credentialsModal.password );
                                    util.toast().success( 'Contraseña copiada al portapapeles' );
                                }}
                            >
                                📋 Copiar
                            </button>
                        </div>
                    </div>

                    <div className="alert alert-warning mt-3">
                        <small>El usuario deberá cambiar esta contraseña en su primer acceso.</small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={() => {
                            setCredentialsModal( { show: false, email: "", password: "" } );
                            reloadTable();
                            handleClose();
                        }}
                    >
                        Listo
                    </button>
                </Modal.Footer>
            </Modal>

            {util.LOADING_SCREEN( loading )}
        </Fragment>
    );
}


function InputCX( { title, onChange, onBlur, value, name, type = null, error = null, mask = null } ) {
    const id = Math.floor( Math.random() * 1000 );
    
    const applyPhoneMask = ( val ) => {
        if ( !val ) return '';
        const cleaned = val.replace( /\D/g, '' );
        if ( cleaned.length <= 4 ) return cleaned;
        if ( cleaned.length <= 6 ) return `${cleaned.slice( 0, 4 )}-${cleaned.slice( 4 )}`;
        if ( cleaned.length <= 8 ) return `${cleaned.slice( 0, 4 )}-${cleaned.slice( 4, 6 )}-${cleaned.slice( 6 )}`;
        return `${cleaned.slice( 0, 4 )}-${cleaned.slice( 4, 6 )}-${cleaned.slice( 6, 8 )}-${cleaned.slice( 8, 10 )}`;
    };
    
    const handleChange = ( e ) => {
        let val = e.target.value;
        if ( mask === 'phone' ) {
            val = applyPhoneMask( val );
            e.target.value = val;
        }
        onChange( e );
    };
    
    return (
        <Fragment>
            <label htmlFor={`id_${id}`}>{title}</label>
            <InputGroup className="mb-2">
                <FormControl
                    name={name}
                    type={type ? type : "text"}
                    id={`id_${id}`}
                    value={value ? value : ""}
                    onChange={handleChange}
                    onBlur={onBlur}
                    className={error ? "is-invalid" : ""}
                    placeholder={mask === 'phone' ? '6865-26-99-12' : ''}
                    maxLength={mask === 'phone' ? 14 : undefined}
                />
            </InputGroup>
            {error && <div className="text-danger small mb-3">{error}</div>}
        </Fragment>
    );
}